import * as R from 'ramda'
import { createSelector } from 'reselect'
import * as RE from 'helpers/ramda-ext'
import { getAllDurations, getStockDurations } from 'domain/pricerDuration'
import { hasVisibleDurationPicker } from 'domain/packages'
import { getFromLocalStorage } from 'store/reducers/fromLocalStorage'
import { getSelectedDepartureCity, getHasVisibleDepartureCityPicker } from './pricerDepartureCity'
import {
  getActivePackage,
  getCurrentSelection,
  getPickersBindings,
  getPickersBindingsStock
} from './package'
import { getDurationsFilterFromQuery } from './router'

export const getAvailableDurations = createSelector(
  getPickersBindings,
  getPickersBindingsStock,
  getSelectedDepartureCity,
  (pickersBindings, pickersBindingsStock, selectedDepartureCity) => {
    const allDurations = getAllDurations(selectedDepartureCity && selectedDepartureCity.key, pickersBindings)

    const stockDurations = getStockDurations(selectedDepartureCity && selectedDepartureCity.key, pickersBindingsStock)

    const sortedDurations = Object.values(allDurations).sort((previousDuration, nextDuration) => (
      previousDuration.nights - nextDuration.nights
    ))

    const result = sortedDurations.map(duration => {
      return {
        ...duration,
        stock: stockDurations && stockDurations[duration.key] ? stockDurations[duration.key].isAvailable : true
      }
    })
    return result
  }
)

export const getHasOnlyOneDuration = createSelector(
  getAvailableDurations,
  RE.hasOneElement
)

export const getSpecificFormatDurationsFromDropdown = createSelector(
  getAvailableDurations,
  getDurationsFilterFromQuery,
  (availableDurations, durationsFromFilters) => {
    const initial = {
      filtered: { title: 'your_durations', values: [] },
      others: { title: 'other_durations', values: [] }
    }

    return availableDurations.reduce((acc, duration) => {
      const foundDuration = durationsFromFilters.find(nbNights => duration.nights === Number(nbNights))
      if (foundDuration) {
        acc.filtered.values.push(duration)
      } else {
        acc.others.values.push(duration)
      }
      return acc
    }, initial)
  }
)

export const getNbAvailableDurationWithStock = createSelector(
  getAvailableDurations,
  R.pipe(
    R.filter(value => value.stock),
    R.length
  )
)

export const getSelectedDuration = createSelector(
  getCurrentSelection,
  getAvailableDurations,
  (currentSelection, availableDurations) => {
    if (!currentSelection || R.isNil(currentSelection.nights)) { return null }

    return R.find(
      R.propEq('nights', currentSelection.nights),
      availableDurations
    )
  }
)

export const getSelectedNbNights = createSelector(
  getSelectedDuration,
  RE.whenNotNil(R.prop('nights'))
)

export const getHasVisibleDurationPicker = createSelector(
  getActivePackage,
  hasVisibleDurationPicker
)

export const getNbNightsFromLocalStorage = createSelector(
  getFromLocalStorage,
  R.path(['configurator', 'nights'])
)

export const getDurationFromLS = createSelector(
  getAvailableDurations,
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getSelectedNbNights,
  getSelectedDepartureCity,
  getNbNightsFromLocalStorage,
  (allDurations, hasVisibleDepartureCityPicker, hasVisibleDurationPicker, selectedNbNights, selectedDepartureCity, nbNightsFromLS) => {
    const hasDepartureCity = Boolean(!hasVisibleDepartureCityPicker || (hasVisibleDepartureCityPicker && selectedDepartureCity))

    if (hasVisibleDurationPicker && R.isNil(selectedNbNights) && hasDepartureCity && RE.isNotNil(nbNightsFromLS)) {
      const isLSAvailable = Boolean(allDurations.find(
        duration => {
          return duration.stock && nbNightsFromLS === duration.nights
        }
      ))

      return isLSAvailable ? nbNightsFromLS : null
    }
    return null
  }
)

export const getUniqDuration = createSelector(
  getHasVisibleDurationPicker,
  getSelectedNbNights,
  getAvailableDurations,
  (hasVisibleDurationPicker, selectedNbNights, availableDurations) => (
    hasVisibleDurationPicker && R.isNil(selectedNbNights) && RE.hasOneElement(availableDurations) && availableDurations[0].stock
      ? availableDurations[0].nights
      : null
  )
)

export const getDurationAutoSelect = createSelector(
  getDurationFromLS,
  getUniqDuration,
  (durationFromLS, durationAutoSelect) => (
    durationFromLS || durationAutoSelect
  )
)
